import {Injectable} from "@angular/core";
import {VisbosService} from "../../core/visbos-core/graphql/visbos.service";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {DosageForm, Medicine} from "../model/medicine.type";
import gql from "graphql-tag";
import {SubstanceGroup} from "../model/substance-group.type";
import {Substance} from "../model/substance.type";
import {MedicineBookEntry} from "../model/medicine-book-entry";
import {Observable} from "rxjs/Observable";
import {ApolloQueryResult} from "apollo-client";
import {Stock} from "../../shared/view-model/stock";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../shared/redux-store";
import {SessionActions} from "../../shared/actions";
import {map} from "rxjs/operators";
import {GenerateMedicineBookMutation, ImportMedicineMutation} from "../../shared/graphql/graphql.types";
import {productFragments} from "./product-services/product.service";

export const substanceFragments = {
  substances: gql`
    fragment SubstanceFields on Substance {
      cuid, label, name,
      substance_group {cuid, label, name}
    }`
};

export const dosageFormFragments = {
  dosageForms: gql`
    fragment DosageFormFields on DosageForm {
      cuid, label
    }`
};

export const medicineFragments = {
  medicine: gql`
    fragment MedicineFields on Medicine {
      cuid, label, is_editable,
      created_at, updated_at,
      name, notes,
      stock_item {cuid, label},
      administration_unit {cuid label},
      dosage_form { ...DosageFormFields },
      capacity, capacity_unit {cuid, label, unit, unit_name}, capacity_string, custom_capacity
      main_substance_dosage_string,
      dosage_per_capacity_string,
      substances {
        cuid,
        dosage,
        dosage_unit {cuid, label, unit, unit_name}
        custom_dosage,
        substance {...SubstanceFields}
      }
    }

    ${substanceFragments.substances}
    ${dosageFormFragments.dosageForms}
  `
};

export const medicineBookFragments = {
  medicineBookEntry: gql`
    fragment MedicineBookEntryFields on MedicineBookEntry {
      cuid label is_editable
      medicine_name type entry_date
      old_stock stock_change new_stock
      consumer_type consumer
      supplier_type supplier
      stock_change_reason_type stock_change_reason
      stock_withdrawal {cuid label}
      stock_increase {cuid label}
      sign_name sign_url is_valid
    }`
};

export const fetchSubstanceGroupsQuery = gql`
  query FetchSubstanceGroups {
    substanceGroups {
      cuid, label, name, label_color, dosage_color
    }
  }`;

export const fetchSubstancesQuery = gql`
  query FetchSubstancesQuery {
    substances {
      ...SubstanceFields
    }
  }
  ${substanceFragments.substances}
`;

export const fetchDosageFormsQuery = gql`
  query FetchDosageFormsQuery {
    dosageForms {
      ...DosageFormFields
    }
  }
  ${dosageFormFragments.dosageForms}
`;

export const fetchMedicineBookEntries = gql`
  query FetchMedicineBookEntries($medicineStockCuid: String!) {
    medicineBookEntries(medicineStockCuid: $medicineStockCuid) {
      ...MedicineBookEntryFields
    }
  }
  ${medicineBookFragments.medicineBookEntry}
`;

export const fetchMedicinesQuery = gql`
  query FetchMedicinesQuery($adminUnitCuid: String, $medicineCuids: [String]) {
    medicines(adminUnitCuid: $adminUnitCuid, medicineCuids: $medicineCuids) {
      ...MedicineFields
    }
  }

  ${medicineFragments['medicine']}
`;

export const fetchMedicineStocksQuery = gql`
  query FetchMedicineStocksQuery($depotCuid: String!) {
    medicineStocks(depotCuid: $depotCuid) {
      cuid label updated_at
      min_stock report_stock shall_stock act_stock
      registration_required
      stock_item {
        category {cuid label}
        cuid label
        medicine {
          ...MedicineFields
        }
      }
    }
  }
  ${medicineFragments.medicine}
`;

export const createMedicineMutation = gql`
  mutation CreateMedicineMutation($medicineInput: MedicineInput!) {
    createMedicine(medicine: $medicineInput) {
      ...MedicineFields
    }
  }
  ${medicineFragments.medicine}
`;

export const updateMedicineMutation = gql`
  mutation UpdateMedicineMutation($medicineCuid: String!, $medicineInput: MedicineInput!) {
    updateMedicine(medicineCuid: $medicineCuid, medicine: $medicineInput) {
      ...MedicineFields
    }
  }
  ${medicineFragments.medicine}
`;

export const deleteMedicineMutation = gql`
  mutation DeleteMedicineMutation($medicineCuid: String!) {
    deleteMedicine(medicineCuid: $medicineCuid)
  }
`;

export const generateMedicineBookPdf = gql`
  mutation GenerateMedicineBook($medicineStocksCuids: [String]) {
    medicineBookPdf(medicineStocksCuids: $medicineStocksCuids)
  }
`;

export const importMedicineMutation = gql`
  mutation ImportMedicine($adminUnitCuid: String, $supplierId: String!, $medicineData: [MedicineImportInput]) {
    importMedicines(administration_unit_id: $adminUnitCuid, supplier_id: $supplierId, medicine_data: $medicineData) {
      cuid, label, name, short_name,
      properties { cuid, name, short_name, value, property { cuid } },
      is_editable,
      category {cuid, label, name},
      administration_unit { cuid, label }
      products { ... ProductFields },
      default_product { ... ProductFields },
      manufacturer { cuid label },
      supplier { cuid label },
      packings { ...ProductPackingFields },
      medicine { ...MedicineFields },
    }
  }
  ${productFragments.product}
  ${productFragments.productPacking}
  ${medicineFragments.medicine}
`;

export interface MedicinesResult {
  medicines: Medicine[];
}

export interface MedicineStocksResult {
  medicineStocks: Stock[];
}

export interface CreateMedicineResult {
  createMedicine: Medicine;
}

export interface UpdateMedicineResult {
  updateMedicine: Medicine;
}

export interface DeleteMedicineResult {
  deleteMedicine: boolean;
}

export interface MedicineBookEntriesResult {
  medicineBookEntries: MedicineBookEntry[]
}

export interface DosageFormsResult {
  dosageForms: DosageForm[];
}

@Injectable()
export class MedicineService extends VisbosService {


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getMedicines(adminUnitCuid?: string | Observable<string>, medicineCuids?: string[]): ApolloQueryObservable<MedicinesResult> {

    let query = this.fetchItems<MedicinesResult>({
      query: fetchMedicinesQuery,
      variables: {
        adminUnitCuid: adminUnitCuid,
        medicineCuids: medicineCuids
      }
    });

    return query;
  }

  public getMedicineStocks(depotCuid: string | Observable<string>): ApolloQueryObservable<MedicineStocksResult> {

    let query = this.fetchItems<MedicineStocksResult>({
      query: fetchMedicineStocksQuery,
      variables: {
        depotCuid: depotCuid
      }
    });

    return query;
  }

  public createMedicine(medicineInput): Observable<ApolloQueryResult<CreateMedicineResult>> {
    return this.mutate({
      mutation: createMedicineMutation,
      variables: {
        medicineInput: medicineInput
      },
      refetchQueries: ['FetchMedicinesQuery']
    })
  }

  public updateMedicine(medicineCuid: string, medicineInput): Observable<ApolloQueryResult<UpdateMedicineResult>> {
    return this.mutate({
      mutation: updateMedicineMutation,
      variables: {
        medicineCuid: medicineCuid,
        medicineInput: medicineInput
      }
    })
  }

  public deleteMedicine(medicineCuid: string): Observable<ApolloQueryResult<DeleteMedicineResult>> {
    return this.mutate({
      mutation: deleteMedicineMutation,
      variables: {
        medicineCuid: medicineCuid
      },
      update: (proxy, result: ApolloQueryResult<DeleteMedicineResult>) => {
        if (result.data.deleteMedicine === true) {
          const vars = {
            adminUnitCuid: this.ngRedux.getState().session.activeAdminUnit,
            medicineCuids: null
          };

          const data: any = proxy.readQuery({
            query: fetchMedicinesQuery,
            variables: vars
          });
          data.medicines.splice(data.medicines.findIndex(med => med.cuid === medicineCuid), 1);

          proxy.writeQuery({
            query: fetchMedicinesQuery,
            data: data,
            variables: vars
          });
        }
      }
    })
  }

  public fetchMedicineBookEntries(medicineStockCuid: string | Observable<string>): ApolloQueryObservable<MedicineBookEntriesResult> {

    let query = this.fetchItems<MedicineBookEntriesResult>({
      query: fetchMedicineBookEntries,
      variables: {
        medicineStockCuid: medicineStockCuid
      }
    });

    return query;
  }

  public getSubstanceGroups(): ApolloQueryObservable<SubstanceGroup[]> {
    let query = this.fetchItems<Medicine[]>({
      query: fetchSubstanceGroupsQuery
    });

    return <ApolloQueryObservable<SubstanceGroup[]>>query.pipe(map(({data}: any) => data.substanceGroups));
  }

  public getSubstances(): ApolloQueryObservable<{ substances: Substance[] }> {
    let query = this.fetchItems<{ substances: Substance[] }>({
      query: fetchSubstancesQuery
    });

    return query;
  }

  public getDosageForms(): ApolloQueryObservable<DosageFormsResult> {
    let query = this.fetchItems<DosageFormsResult>({
      query: fetchDosageFormsQuery
    });
    return query;
  }

  public generateMedicineBookPdf(medicineStocksCuids: string[]): Observable<ApolloQueryResult<GenerateMedicineBookMutation>> {
    return this.mutate({
      mutation: generateMedicineBookPdf,
      variables: {
        medicineStocksCuids: medicineStocksCuids
      }
    })
  }

  public importMedicines(supplierId: string, medicineData, adminUnitCuid?: string): Observable<ApolloQueryResult<ImportMedicineMutation>> {
    return this.mutate({
      mutation: importMedicineMutation,
      variables: {
        adminUnitCuid: adminUnitCuid,
        supplierId: supplierId,
        medicineData: medicineData
      },
      refetchQueries: ['FetchStockItemsQuery', 'FetchStockItemsWithParentsQuery']
    })
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/medicine.service.ts